import {
  Box,
  Slider,
  Typography,
  Button
} from '@mui/material';

import { useMemo } from 'react';

import { useHourFilterStore } from '../stores/useHourFilterStore';
import { usePermissions } from '../hooks/auth/usePermissions';
import { useDateStore } from '../stores/useDateStore';
import { useOperationalMapStore } from '../stores/useOperationalMapStore';
import { getOperationalContext, getNextOperationalChange } from '../utils/dateUtils';
import { useLayoutStore } from '../hooks/screen/useLayoutStore';
import { useLoadingStore, selectIsMainLoading } from '../stores/useLoadingStore';

function formatHour(value, showNextDay = false) {
  
  const hour = value % 24;

  const formatted = `${String(hour).padStart(2, '0')}:00`;

  if (showNextDay && value >= 24) {
    return `${formatted} +1`;
  }

  return formatted;
}

export default function HourRangeFilter() {

  const { hourRangePerm } = usePermissions();

  const hourRange = useHourFilterStore(state => state.hourRange);

  const setHourRange = useHourFilterStore(state => state.setHourRange);

  const autoOperationalRange = useDateStore(state => state.autoOperationalRange);

  const setAutoOperationalRange = useDateStore(state => state.setAutoOperationalRange);

  const setSelectedDate = useDateStore(state => state.setSelectedDate);

  const isMainLoading = useLoadingStore(selectIsMainLoading);

  const isCompactMode = useLayoutStore(
    state => state.isCompactMode
  );

  const isOperationalMapMode = useOperationalMapStore(
    state => state.isOperationalMapMode
  );

  const {minHour, maxHour} = hourRangePerm;

  const nextOperationalChange = useMemo(() => {

  if (
      !hourRangePerm.useOperationalRange ||
      !autoOperationalRange
    ) {
      return null;
    }

    return getNextOperationalChange();

  }, [
    hourRangePerm.useOperationalRange,
    autoOperationalRange
  ]);

  const handleRestoreOperationalRange = () => {

    const {
      date,
      hourRange: operationalRange
    } = getOperationalContext();

    setSelectedDate(
      date,
      true,
      false
    );

    setHourRange(
      operationalRange,
      true,
      false
    );

    setAutoOperationalRange(true);
  };

  const marks = useMemo(() => {

    const isOperationalRange =
      minHour === 0 &&
      maxHour === 32;

    const IMPORTANT_HOURS = [8, 20, 32];

    return Array.from(
      { length: maxHour - minHour + 1 },
      (_, index) => {

        const hour = minHour + index;

        const isImportant =
          isOperationalRange &&
          IMPORTANT_HOURS.includes(hour);

        const showLabel =
          isOperationalRange
            ? (
                isImportant ||
                hour % 4 === 0 ||
                hour === minHour ||
                hour === maxHour
              )
            : (
                hour % 3 === 0 ||
                hour === minHour ||
                hour === maxHour
              );

        return {
          value: hour,
          label: !isCompactMode && showLabel
            ? (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    fontWeight: isImportant
                      ? 900
                      : 400
                  }}
                >
                  {formatHour(hour, true)}
                </Typography>
              )
            : ''
        };

      }
    );

  }, [
    minHour,
    maxHour,
    isCompactMode
  ]);
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        left: 0,
        bottom: 0,
        width: '100%',
        px: 6,
        pt: 1,
        pl: (isCompactMode && !isOperationalMapMode) ? 10 : undefined,
        mt: isCompactMode ? 0 : 2,
        pb: isCompactMode ? 1 : 0,
        bgcolor: isCompactMode ? 'background.paper' : 'unset',
        opacity: isMainLoading ? .5 : 1,
        pointerEvents: isMainLoading ? 'none' : 'auto'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isCompactMode ? 1 : 2
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: isCompactMode
              ? '0.75rem'
              : undefined
          }}
        >
          Horário: {formatHour(hourRange[0])} até {formatHour(hourRange[1], true)}
        </Typography>

        {hourRangePerm.useOperationalRange && !isOperationalMapMode && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: isCompactMode ? 0.5 : 1
            }}
          >
            {autoOperationalRange ? (
              <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: isCompactMode
                      ? '0.65rem'
                      : undefined
                  }}
                >
                Próxima virada operacional:{' '}
                <strong>
                  {nextOperationalChange?.format(
                    'DD/MM/YYYY - HH:mm'
                  )}
                </strong>
              </Typography>
            ) : (
              <>
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{
                    fontWeight: 600,
                    fontSize: isCompactMode
                      ? '0.65rem'
                      : undefined
                  }}
                >
                  Virada operacional automática desativada
                </Typography>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRestoreOperationalRange}
                  sx={{
                    ...(isCompactMode && {
                      fontSize: '0.65rem',
                      px: 1,
                      py: 0.25,
                      minWidth: 'auto',
                      lineHeight: 1.3
                    })
                  }}
                >
                  Voltar ao automático
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
      <Slider
        value={hourRange}
        min={minHour}
        max={maxHour}
        step={1}
        marks={marks}
        valueLabelDisplay="auto"
        valueLabelFormat={value =>
          formatHour(value, true)
        }
        onChange={(_, value) => {
          setHourRange(value);
        }}
        sx={{
          ...(isCompactMode && {
            // Linha preenchida
            '& .MuiSlider-track': {
              height: 2
            },

            // Linha de fundo
            '& .MuiSlider-rail': {
              height: 2
            },

            // Bolinhas das extremidades
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10
            },

            // Marcadores das horas
            '& .MuiSlider-mark': {
              width: 2,
              height: 2,
              borderRadius: '50%'
            },

            // Esconde os horários inferiores
            '& .MuiSlider-markLabel': {
              display: 'none'
            }
          })
        }}
      />
    </Box>
  );
}